import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import i18n from "../javascripts/util/i18n"

// Generic drag-and-drop for the module editor (and anywhere else that needs
// "move an item from one container into another, then persist the new parent").
//
// One controller instance == one droppable list. Lists that share the same
// `group` value can exchange items; the `accepts` value gates, per list, which
// item *types* may land there (that is the rule/nesting hook — a list only
// accepts the types it names). On a cross-list drop the moved item is PATCHed to
// its own `update-url` with `param` set to this list's `parent-id`.
//
// Markup contract:
//   list (the controller element):
//     data-controller="dnd"
//     data-dnd-group-value="variables"                  # SortableJS channel
//     data-dnd-accepts-value="variable"                 # space-separated types
//     data-dnd-parent-id-value="<id or ''>"             # parent items get here
//     data-dnd-param-value="variable[variable_group_id]"# PATCH body key
//     data-dnd-empty-label-value="No variables yet"     # placeholder text
//     data-dnd-sort-value="true"                        # allow in-list reorder
//   item (direct child of the list):
//     data-dnd-item
//     data-dnd-type="variable"
//     data-dnd-update-url="/backoffice/modules/1/variables/2"
//
// Uses SortableJS in forced-fallback mode so the drag image is a styled clone of
// the row (not the browser's native link ghost) and Chrome never offers its
// link split-view — both native-HTML5-drag quirks we hit with the old per-type
// controllers this replaces.
export default class extends Controller {
  static values = {
    group: String,
    accepts: String,
    parentId: String,
    param: String,
    emptyLabel: String,
    sort: { type: Boolean, default: false },
    handle: String,
    // Optional: id of a turbo-frame to re-fetch after a successful move, so any
    // detail/list view rendered there reflects the new parent. The list itself
    // already moves optimistically; this refreshes *other* views of the same
    // data (e.g. the variables list / variable detail / group detail pages all
    // live in #module_content). Left unset, nothing extra is reloaded.
    refreshFrame: String,
  }

  connect() {
    this.disableAnchorDragging()

    const accepts = this.acceptsValue.split(/\s+/).filter(Boolean)

    this.sortable = Sortable.create(this.element, {
      group: {
        name: this.groupValue,
        pull: true,
        // Per-list acceptance rule: empty `accepts` means "anything in this
        // group", otherwise the dragged item's type must be listed.
        put: (_to, _from, dragEl) =>
          accepts.length === 0 || accepts.includes(dragEl.dataset.dndType),
      },
      draggable: "[data-dnd-item]",
      // The group header lives inside the list (so the whole group, label
      // included, is one drop target) but must never be dragged or sorted.
      filter: "[data-dnd-empty], [data-dnd-header]",
      // Keep clicks on filtered elements (header toggle/label/add) working —
      // without this, fallback mode preventDefaults the mousedown and the click
      // is swallowed.
      preventOnFilter: false,
      handle: this.handleValue || undefined,
      sort: this.sortValue,
      animation: 150,
      // Require a brief hold + small move so a slow trackpad click is never a drag.
      delay: 80,
      delayOnTouchOnly: false,
      touchStartThreshold: 5,
      fallbackTolerance: 5,
      forceFallback: true,
      fallbackOnBody: true,
      ghostClass: "dnd-placeholder",
      chosenClass: "dnd-chosen",
      dragClass: "dnd-drag",
      fallbackClass: "dnd-fallback",
      onStart: () => document.body.classList.add("dnd-active"),
      onEnd: () => document.body.classList.remove("dnd-active"),
      onAdd: (event) => this.onAdd(event),
    })
  }

  disconnect() {
    this.sortable?.destroy()
    this.sortable = null
    document.body.classList.remove("dnd-active")
  }

  // Fires on the destination list when an item is dropped in from another list.
  onAdd(event) {
    const item = event.item
    const url = item.dataset.dndUpdateUrl

    this.removeEmptyPlaceholder(this.element)
    this.ensureEmptyPlaceholder(event.from)

    // If the item was dropped onto a collapsed group, expand it so the result is
    // visible (the group's Alpine wrapper listens for dnd:expand).
    if (this.element.classList.contains("dnd-collapsed")) {
      this.element.dispatchEvent(new CustomEvent("dnd:expand"))
    }

    if (!url || !this.paramValue) return

    this.persist(url, this.paramValue, this.parentIdValue).then(
      // Two-arg form so a refresh error never triggers the revert below — that
      // path is only for an actual failed persist.
      () => this.refreshContentFrame(),
      () => {
        // Persist failed — undo the optimistic move back to the source position.
        const ref = event.from.children[event.oldIndex] || null
        event.from.insertBefore(item, ref)
        this.removeEmptyPlaceholder(event.from)
        this.ensureEmptyPlaceholder(this.element)
      }
    )
  }

  // Re-fetch the configured content frame so views of the moved item (its detail
  // page, the list it appears in, the destination/source group pages) pick up the
  // new parent. After in-module navigation the frame has a `src` we can reload;
  // on a fresh full-page load it doesn't, so point it at the current URL.
  refreshContentFrame() {
    const frameId = this.refreshFrameValue
    if (!frameId) return
    const frame = document.getElementById(frameId)
    if (!frame) return
    if (frame.src) frame.reload()
    else frame.src = window.location.href
  }

  persist(url, param, parentId) {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const body = new URLSearchParams()
    body.append(param, parentId ?? "")

    return fetch(url, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": token || "",
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
      credentials: "same-origin",
      body,
    }).then((response) => {
      if (!response.ok) throw new Error("Drag-and-drop move failed")
    })
  }

  // Anchors are natively draggable, so even in fallback mode grabbing one can
  // kick off a browser link drag (URL ghost + Chrome split-view). No anchor here
  // should be a drag source — SortableJS drives the drag from the row instead.
  disableAnchorDragging() {
    this.element.querySelectorAll("a").forEach((link) => {
      link.setAttribute("draggable", "false")
    })
  }

  removeEmptyPlaceholder(list) {
    const placeholder = list?.querySelector("[data-dnd-empty]")
    if (!placeholder) return
    placeholder.remove()
    // Let the group header react (re-enable its collapse caret).
    list.dispatchEvent(new CustomEvent("dnd:filled", { bubbles: true }))
  }

  ensureEmptyPlaceholder(list) {
    if (!list || list.querySelector("[data-dnd-item]")) return
    if (list.querySelector("[data-dnd-empty]")) return

    const placeholder = document.createElement("li")
    placeholder.dataset.dndEmpty = "true"
    placeholder.className = "menu-empty"
    placeholder.textContent = list.dataset.dndEmptyLabelValue || i18n.t("js.dnd.empty")
    list.appendChild(placeholder)
    // Let the group header react (disable its collapse caret, fold it shut).
    list.dispatchEvent(new CustomEvent("dnd:emptied", { bubbles: true }))
  }
}
